import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modal con focus trap básico, cierre por Escape y scroll lock del body.
 * Para Fase 3 podemos migrar a @radix-ui/react-dialog si necesitamos
 * accesibilidad WAI-ARIA completa, pero esto cubre el 90% de casos.
 */
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);

  // Mantenemos la última referencia de onClose sin que el effect de abajo
  // dependa de ella. Si dependiera, cada render del padre (que suele pasar un
  // closure nuevo) re-ejecutaría el focus inicial y le robaría el foco al
  // input en cada tecla.
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKey);

    // Foco inicial al abrir. Si un hijo ya tomó el foco (ej: un Input con
    // autoFocus), lo respetamos en vez de pisarlo con el primer botón.
    if (!dialogRef.current?.contains(document.activeElement)) {
      const focusable = dialogRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        onClick={(e) => e.stopPropagation()}
        className={[
          'w-full bg-white shadow-xl',
          'animate-slide-up rounded-t-2xl sm:rounded-2xl',
          sizeClasses[size],
        ].join(' ')}
      >
        {title && (
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 id="modal-title" className="text-lg font-semibold text-slate-900">
              {title}
            </h2>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;